/**
 * The browser profile's manifest formatters (ADR-0014, ADR-0016): the
 * Content-Security-Policy and Permissions-Policy a page would need to grant
 * for the script to work as observed. core/manifest composes them.
 */
import type { AuditEvent } from "../../events";
import { originOf, resolveUrl } from "../../url";

/** CSP directives in the order the manifest lists them. */
export const CSP_DIRECTIVES = [
  "connect-src",
  "script-src",
  "style-src",
  "img-src",
  "font-src",
  "media-src",
  "frame-src",
  "object-src",
  "worker-src",
  "form-action",
] as const;
export type CspDirective = (typeof CSP_DIRECTIVES)[number];

const SELF = "'self'";
const UNSAFE_INLINE = "'unsafe-inline'";
const UNSAFE_EVAL = "'unsafe-eval'";
const FONT_FILE = /\.(woff2?|ttf|otf|eot)([?#]|$)/i;

/** Network kinds that are `connect-src` requests. */
const CONNECT_KINDS = new Set(["fetch", "xhr", "beacon", "websocket", "eventsource"]);

/** The directive a resource load falls under, by the element that loaded it. */
function resourceDirective(target: string, url: string): CspDirective | null {
  const iface = target.slice(0, target.indexOf("."));
  switch (iface) {
    case "HTMLScriptElement":
      return "script-src";
    case "HTMLImageElement":
      return "img-src";
    case "HTMLIFrameElement":
    case "HTMLFrameElement":
      return "frame-src";
    case "HTMLLinkElement":
      return FONT_FILE.test(url) ? "font-src" : "style-src";
    case "HTMLVideoElement":
    case "HTMLAudioElement":
    case "HTMLSourceElement":
      return "media-src";
    case "HTMLEmbedElement":
    case "HTMLObjectElement":
      return "object-src";
    default:
      return null;
  }
}

/** A CSP source expression for `url`: `'self'`, an origin, or a scheme (`data:`, `blob:`). */
function sourceFor(url: string, pageUrl: string): string | null {
  const scheme = /^(data|blob):/i.exec(url);
  if (scheme?.[1]) return `${scheme[1].toLowerCase()}:`;
  const origin = originOf(url);
  if (origin === "null") return null;
  return origin === originOf(pageUrl) ? SELF : origin;
}

/** Whether written HTML carries inline styles. */
const INLINE_STYLE = /<style\b|\sstyle\s*=/i;
const HTML_SINK = /\.(innerHTML|outerHTML|insertAdjacentHTML|write|writeln)$/;

/** The CSP directives (with sources) the script's observed behavior needs. */
export function cspFor(events: readonly AuditEvent[], pageUrl: string): Record<string, string[]> {
  const sources = new Map<CspDirective, string[]>();
  const add = (directive: CspDirective, source: string | null) => {
    if (source === null) return;
    const list = sources.get(directive) ?? [];
    if (!list.includes(source)) list.push(source);
    sources.set(directive, list);
  };
  const keywords = new Map<CspDirective, string[]>();
  const addKeyword = (directive: CspDirective, keyword: string) => {
    const list = keywords.get(directive) ?? [];
    if (!list.includes(keyword)) list.push(keyword);
    keywords.set(directive, list);
  };
  for (const e of events) {
    const net = e.network;
    if (net && CONNECT_KINDS.has(net.kind)) add("connect-src", sourceFor(net.url, pageUrl));
    else if (net?.kind === "resource") {
      const directive = resourceDirective(e.target, net.url);
      if (directive) add(directive, sourceFor(net.url, pageUrl));
    } else if (net?.kind === "navigation" && e.target.startsWith("HTMLFormElement.")) {
      add("form-action", sourceFor(net.url, pageUrl));
    }
    const moduleUrl = e.codegen?.module?.url;
    if (e.op === "codeload" && moduleUrl) add("script-src", sourceFor(moduleUrl, pageUrl));
    if (e.target === "navigator.serviceWorker.register") {
      const [script] = e.args;
      const url = typeof script === "string" ? resolveUrl(script, pageUrl) : null;
      add("worker-src", url === null ? SELF : sourceFor(url, pageUrl));
    }
    if (e.tags.includes("unsafe-inline")) addKeyword("script-src", UNSAFE_INLINE);
    if (e.tags.includes("unsafe-eval")) addKeyword("script-src", UNSAFE_EVAL);
    if (e.category === "dom" && HTML_SINK.test(e.target)) {
      const html = e.args.filter((a): a is string => typeof a === "string").join("");
      if (INLINE_STYLE.test(html)) addKeyword("style-src", UNSAFE_INLINE);
    }
  }
  const csp: Record<string, string[]> = {};
  for (const directive of CSP_DIRECTIVES) {
    const list = [...(sources.get(directive) ?? [])];
    // 'self' first, then origins in first-seen order, then keywords.
    list.sort((a, b) => Number(b === SELF) - Number(a === SELF));
    const all = [...list, ...(keywords.get(directive) ?? []).sort()];
    if (all.length > 0) csp[directive] = all;
  }
  return csp;
}

/** `connect-src 'self' https://a.example; img-src data:` */
export const formatCsp = (csp: Record<string, string[]>): string =>
  Object.entries(csp)
    .map(([directive, list]) => [directive, ...list].join(" "))
    .join("; ");

const PERMISSION_TAG = "permission:";

/** Permission-gated features (`permission:<feature>` tags) the script used, in first-seen order. */
export function permissionsFor(events: readonly AuditEvent[]): string[] {
  const features: string[] = [];
  for (const e of events) {
    for (const tag of e.tags) {
      if (!tag.startsWith(PERMISSION_TAG)) continue;
      const feature = tag.slice(PERMISSION_TAG.length);
      if (feature !== "" && !features.includes(feature)) features.push(feature);
    }
  }
  return features;
}

/** `geolocation=(self), clipboard-read=(self)` */
export const formatPermissionsPolicy = (features: readonly string[]): string =>
  features.map((f) => `${f}=(self)`).join(", ");
